import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useGetPresignedUrl from '@/api-client/use-presigned-url';
import { ModuleName } from '@/constant/modules.constants';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { OrgCreationProgressDialog } from '../components/org-creation-progress-dialog';
import { useMyOrganizations } from '@/graphql/queries/organizations';
import { useCreateOrganization } from '@/api-client/use-create-orginization';
import { fetchMyOrganizations } from '@/api-client/use-my-ognization';
import {
  setupOrganization,
  type OrganizationSetupStep,
} from '@/api-client/use-create-orginization/setup-organization';
import { useInsertOrganization } from '@/graphql/mutations/organizations';
import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '@/store';
import { getDisplayErrorMessage } from '@/lib/utils/api-error';
import { useToast } from '@/hooks/use-toast';
import { Divider, LoadingOverlay } from '@/components/core';
import { Avatar, AvatarFallback } from '@/components/ui-kit/avatar';
import { v4 as uuid } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { useSwitchOrg } from '@/api-client/use-swich-organization';
import { useAuthStore } from '@/state/store/auth';
import { orgPath } from '../utils/org-path';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
const MAX_SIZE_MB = 2;

const OrgNewPage = () => {
  const { t } = useTranslation();
  const [orgName, setOrgName] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getPresignedUrlAsync } = useGetPresignedUrl();
  const { data: dbOrgs, isLoading } = useMyOrganizations();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const setupUser = useUserStore((state) => state.user);
  const { createOrganizationAsync } = useCreateOrganization();
  const { insertOrganizationAsync } = useInsertOrganization();
  const [progressOpen, setProgressOpen] = useState(false);
  const [steps, setSteps] = useState<OrganizationSetupStep[]>([]);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const processingRef = useRef(false);
  const navigate = useNavigate();
  const { mutateAsync: switchOrganizationAsync } = useSwitchOrg();

  const closeProgress = () => {
    if (processingRef.current) return;
    setProgressOpen(false);
    setSteps([]);
  };

  const createOrganization = async (
    name: string,
    signatureOrgId?: string,
    uploadLogo?: () => Promise<string | null>
  ) => {
    if (processingRef.current) return;
    if (!setupUser?.itemId) {
      setProvisionError('Please sign in before creating an organization.');
      setProgressOpen(true);
      return;
    }
    processingRef.current = true;
    setIsProcessing(true);
    setProvisionError(null);
    setSteps([]);
    setProgressOpen(true);
    try {
      const org = await setupOrganization(
        { name, signatureOrgId, uploadLogo, user: setupUser },
        {
          createOrganization: createOrganizationAsync,
          insertOrganization: insertOrganizationAsync,
          getOrganizations: fetchMyOrganizations,
          onProgress: setSteps,
        }
      );
      await switchOrganizationAsync({
        organization_id: org.BlocksOrgId,
        organization_name: name,
      });
      useAuthStore.getState().setOrg(org.BlocksOrgId, org.Slug, org.ItemId, org.SigOrgId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['me'] }),
        queryClient.invalidateQueries({ queryKey: ['my-ognization'] }),
        queryClient.invalidateQueries({ queryKey: ['my-uds-organizations'] }),
      ]);
      toast({ variant: 'success', title: t('COMMON_CREATEORGANIZATION') });
      navigate(orgPath(org.BlocksOrgId));
    } catch (error) {
      setProvisionError(getDisplayErrorMessage(error));
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
    }
  };

  const handleFile = (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Only PNG, JPG, SVG, or WebP images are allowed.');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`File size must be under ${MAX_SIZE_MB}MB.`);
      return;
    }
    setError(null);
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, []);

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => setIsDragging(false);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const removeLogo = () => {
    setLogo(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadLogo = async (file: File): Promise<string | null> => {
    try {
      const data = await getPresignedUrlAsync({
        name: file.name,
        itemId: uuid(),
        metaData: '',
        accessModifier: 'Public',
        configurationName: 'Default',
        parentDirectoryId: '',
        tags: 'Logo',
        moduleName: ModuleName.DefaultConstruct,
      });

      if (!data.isSuccess || !data.uploadUrl) {
        setError('Failed to get upload URL for logo.');
        return null;
      }

      await fetch(data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
          'x-ms-blob-type': 'BlockBlob',
        },
      });

      return data.uploadUrl.split('?')[0];
    } catch {
      setError('Failed to upload logo.');
      return null;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgName.trim()) return;
    void createOrganization(orgName, undefined, logo ? () => uploadLogo(logo) : undefined);
  };

  if (isLoading) {
    return <LoadingOverlay />;
  }

  const sigOrgs = dbOrgs?.sigOrgsWithoutBlocks ?? [];

  return (
    <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center px-4 py-12">
      <OrgCreationProgressDialog
        open={progressOpen}
        steps={steps}
        error={provisionError}
        onClose={closeProgress}
      />
      <div className="w-full max-w-lg">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-foreground">
            {t('ONBOARDING_CREATEORG_TITLE')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('ONBOARDING_CREATEORG_DESCRIPTION')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          {/* Organization Name */}
          <div className="space-y-2">
            <Label htmlFor="org-name">{t('ONBOARDING_FORM_ORGNAME')}</Label>
            <Input
              id="org-name"
              placeholder={t('ONBOARDING_FORM_ORGNAMEPLACEHOLDER')}
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">{t('ONBOARDING_FORM_ORGNAMEHELP')}</p>
          </div>

          {/* Logo Upload */}
          <div className="space-y-2">
            <Label>Logo</Label>

            {logoPreview ? (
              <div className="flex items-center gap-4 rounded-lg border bg-background p-4">
                <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md border">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="h-full w-full object-contain"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{logo?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {logo && `${(logo.size / 1024).toFixed(1)} KB`}
                  </p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={removeLogo}>
                  Remove
                </Button>
              </div>
            ) : (
              <div
                className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 transition-colors ${
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <p className="text-sm font-medium">Drop your logo here or click to browse</p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG, SVG, WebP (max {MAX_SIZE_MB}MB)
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_TYPES.join(',')}
                  className="hidden"
                  onChange={onFileChange}
                />
              </div>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isProcessing || !orgName.trim()}>
            {isProcessing ? `${t('COMMON_CREATEORGANIZATION')}...` : t('COMMON_CREATEORGANIZATION')}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            {t('ONBOARDING_CREATEORG_HELPER')}
          </p>
        </form>
        {sigOrgs.length > 0 && (
          <div className="pt-4 mb-3">
            <Divider className="py-4" text={'Continue With SELISE Signature Organizations'} />
            <div className="mt-2 space-y-2">
              {sigOrgs.map(
                (org: { OrganizationId: string; OrganizationName: string; Role: string }) => (
                  <button
                    key={org.OrganizationId}
                    type="button"
                    disabled={isProcessing}
                    onClick={() =>
                      void createOrganization(org.OrganizationName, org.OrganizationId)
                    }
                    className="flex w-full items-center gap-4 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarFallback className="bg-selise-blue-500 text-white text-sm font-semibold">
                        {org.OrganizationName.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <span className="block truncate font-medium text-foreground">
                        {org.OrganizationName}
                      </span>

                      <span className="block text-xs text-muted-foreground">Company Admin</span>
                    </div>
                  </button>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgNewPage;
